import React from 'react';
import { Progress, Space, Tooltip, Typography } from 'antd';
import { WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { TrailerCapacityIndicator } from './sharedTransportFormUtils';
import { formatLocalizedNumber } from '../utils/numberFormatting';

const { Text } = Typography;

// F1 (trailer capacity indicator, medium): compact display-only badge showing
// utilization vs a standard trailer (13.6 LDM / 24 000 kg — single home
// TRAILER_CAPACITY_LDM/TRAILER_PAYLOAD_KG in transportCostAllocation.ts, both
// mirrors). NEVER a save-blocking limit — this is purely informational, so it
// renders as a small Progress bar + compact "loaded / capacity unit" label,
// green under capacity and red + a warning tooltip once over 100%.
//
// Used by the cargo stats row in the single-truck transport form.
interface TransportCapacityBadgeProps {
  indicator: TrailerCapacityIndicator;
  unitLabel: string; // 'LDM' or 'kg'
  precision: number; // decimal places for the loaded/capacity numbers
}

const TransportCapacityBadge: React.FC<TransportCapacityBadgeProps> = ({ indicator, unitLabel, precision }) => {
  const { t, i18n } = useTranslation(['transport']);
  const percentForBar = Math.min(indicator.percent, 100);
  const strokeColor = indicator.overCapacity ? '#ff4d4f' : '#52c41a';

  const label = `${formatLocalizedNumber(indicator.loaded, precision, i18n.language)} / ${formatLocalizedNumber(indicator.capacity, precision, i18n.language)} ${unitLabel}`;

  return (
    <Space size={6} align="center">
      <Progress
        type="circle"
        percent={percentForBar}
        size={28}
        strokeColor={strokeColor}
        format={() => ''}
      />
      <Text style={{ fontSize: 12 }} type={indicator.overCapacity ? 'danger' : undefined}>
        {label}
      </Text>
      {indicator.overCapacity ? (
        <Tooltip title={t('transport:capacity.overCapacityTooltip')}>
          <WarningOutlined style={{ color: '#ff4d4f' }} />
        </Tooltip>
      ) : null}
    </Space>
  );
};

export default TransportCapacityBadge;
